import React, { useRef } from 'react';
import { motion, useMotionValue, useSpring, useReducedMotion } from 'framer-motion';

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & { as?: 'button' };
type AnchorProps = React.AnchorHTMLAttributes<HTMLAnchorElement> & { as: 'a' };

type Props = (ButtonProps | AnchorProps) & {
  children: React.ReactNode;
  strength?: number;
  radius?: number;
  className?: string;
};

export const MagneticButton: React.FC<Props> = (props) => {
  const { children, strength = 0.35, radius = 90, className, ...rest } = props as any;
  const ref = useRef<HTMLElement | null>(null);
  const reduce = useReducedMotion();

  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const springX = useSpring(x, { stiffness: 220, damping: 16, mass: 0.4 });
  const springY = useSpring(y, { stiffness: 220, damping: 16, mass: 0.4 });

  const onMove = (e: React.MouseEvent) => {
    if (reduce) return;
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = e.clientX - cx;
    const dy = e.clientY - cy;
    const dist = Math.hypot(dx, dy);
    if (dist > rect.width / 2 + radius) {
      x.set(0);
      y.set(0);
      return;
    }
    x.set(dx * strength);
    y.set(dy * strength);
  };

  const onLeave = () => {
    x.set(0);
    y.set(0);
  };

  const isAnchor = (rest as any).as === 'a' || 'href' in rest;
  const MotionTag: any = isAnchor ? motion.a : motion.button;

  return (
    <MotionTag
      ref={ref as any}
      className={className}
      style={{ x: reduce ? 0 : springX, y: reduce ? 0 : springY, display: 'inline-flex' }}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      {...rest}
    >
      {children}
    </MotionTag>
  );
};
